import type { SessionEntry } from '../types'
import type { State } from '../state'
import { icons } from './icons'
import { escapeHtml } from './highlight'

export interface DrawerCallbacks {
  onToggleArm(): void
  onSelectTool(tool: 'picker' | 'marker'): void
  onToggleScreenshot(): void
  onExit(): void
  onRowClick(entry: SessionEntry): void
  onRowCopy(entry: SessionEntry): void
}

export interface DrawerOptions {
  dock: 'left' | 'right'
  layout: 'overlay' | 'push'
}

const LS_KEY = 'qc-lens.drawer'
interface DrawerPrefs {
  collapsed: boolean
  dock: 'left' | 'right'
  layout: 'overlay' | 'push'
}

function loadPrefs(dock: 'left' | 'right', layout: 'overlay' | 'push'): DrawerPrefs {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return { collapsed: false, dock, layout, ...JSON.parse(raw) }
  } catch {
    /* ignore */
  }
  return { collapsed: false, dock, layout }
}
function savePrefs(p: DrawerPrefs): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(p))
  } catch {
    /* ignore */
  }
}

// Toolkit drawer per ui-ux-plan §4.1: header / Tools / This Session / footer CTA.
// Phase 2: collapse-to-rail with persistence, occlusion ghosting, session-row actions.
export class Drawer {
  readonly el: HTMLElement
  // Must match .qc-drawer / .qc-drawer.qc-collapsed in ui/styles.ts. When these
  // disagreed (300/44 vs 320/48) the push offset, dialog insets and under-drawer
  // detection were all short by 20 px.
  readonly width = 320
  readonly railWidth = 48
  private prefs: DrawerPrefs
  private dot!: HTMLElement
  private pickerRow!: HTMLButtonElement
  private pickerBadge!: HTMLElement
  private markerRow!: HTMLButtonElement
  private markerBadge!: HTMLElement
  private shotBadge!: HTMLElement
  private sessionList!: HTMLElement
  private sessionCount!: HTMLElement
  private cta!: HTMLButtonElement
  private railCount!: HTMLElement
  private layoutBtn!: HTMLButtonElement
  private offsetApplied = false
  private savedHtmlStyle: string | null = null
  private cbs: DrawerCallbacks

  constructor(root: ShadowRoot, opts: DrawerOptions, cbs: DrawerCallbacks) {
    this.cbs = cbs
    this.prefs = loadPrefs(opts.dock, opts.layout)
    this.el = document.createElement('aside')
    this.el.setAttribute('role', 'complementary')
    this.el.setAttribute('aria-label', 'QC Lens evaluation tools')
    this.el.innerHTML = `
      <div class="qc-drawer-full" data-ref="full">
        <div class="qc-drawer-head">
          <span class="qc-dot" data-ref="dot"></span>
          <span class="qc-title">QC Lens</span>
          <button class="qc-iconbtn" data-ref="layout" aria-pressed="false" aria-label="Dock beside page"
            title="Dock beside page — the page resizes to fit">${icons.panel}</button>
          <button class="qc-iconbtn" data-ref="collapse" aria-label="Collapse drawer" title="Collapse">${icons.collapse}</button>
          <button class="qc-iconbtn" data-ref="exit" aria-label="Exit evaluation mode" title="Exit evaluation mode">${icons.x}</button>
        </div>
        <div class="qc-section">
          <div class="qc-section-label">Tools</div>
          <button class="qc-tool" data-ref="picker">
            ${icons.crosshair}
            <span class="qc-tool-name">Element picker</span>
            <span class="qc-badge" data-ref="pickerBadge">active</span>
          </button>
          <button class="qc-tool" data-ref="marker">
            ${icons.pen}
            <span class="qc-tool-name">Marker: circle an area</span>
            <span class="qc-badge" data-ref="markerBadge"></span>
          </button>
          <button class="qc-tool qc-toggle" data-ref="shot">
            ${icons.camera}
            <span class="qc-tool-name">Screenshot</span>
            <span class="qc-badge" data-ref="shotBadge">on</span>
          </button>
        </div>
        <div class="qc-section qc-session-wrap" style="flex:1;display:flex;flex-direction:column;min-height:0;border-bottom:0;">
          <div class="qc-section-label">This session <span data-ref="count"></span></div>
          <div class="qc-session" data-ref="session">
            <div class="qc-session-empty">No reports yet. Click any element on the page to file the first one.</div>
          </div>
        </div>
        <div class="qc-drawer-foot">
          <div class="qc-hint"><kbd>Esc</kbd> back · <kbd>↑</kbd><kbd>↓</kbd> parent/child · <kbd>Ctrl+Enter</kbd> submit</div>
          <button class="qc-cta" data-ref="cta">${icons.crosshair}<span>Pick an element to report</span></button>
        </div>
      </div>
      <div class="qc-rail" data-ref="rail">
        <button class="qc-iconbtn" data-ref="expand" aria-label="Expand drawer" title="Expand">${icons.lens}</button>
        <button class="qc-iconbtn qc-rail-picker" data-ref="railPicker" aria-label="Toggle element picker">${icons.crosshair}</button>
        <span class="qc-rail-count" data-ref="railCount" title="Reports this session">0</span>
        <button class="qc-iconbtn" data-ref="railExit" aria-label="Exit evaluation mode">${icons.x}</button>
      </div>
    `
    this.dot = this.ref('dot')
    this.pickerRow = this.ref('picker')
    this.pickerBadge = this.ref('pickerBadge')
    this.markerRow = this.ref('marker')
    this.markerBadge = this.ref('markerBadge')
    this.shotBadge = this.ref('shotBadge')
    this.sessionList = this.ref('session')
    this.sessionCount = this.ref('count')
    this.cta = this.ref('cta')
    this.railCount = this.ref('railCount')
    this.layoutBtn = this.ref('layout')

    this.ref<HTMLButtonElement>('exit').addEventListener('click', () => this.cbs.onExit())
    this.ref<HTMLButtonElement>('railExit').addEventListener('click', () => this.cbs.onExit())
    this.pickerRow.addEventListener('click', () => this.cbs.onSelectTool('picker'))
    this.markerRow.addEventListener('click', () => this.cbs.onSelectTool('marker'))
    this.ref<HTMLButtonElement>('shot').addEventListener('click', () => this.cbs.onToggleScreenshot())
    this.ref<HTMLButtonElement>('railPicker').addEventListener('click', () => this.cbs.onToggleArm())
    this.cta.addEventListener('click', () => this.cbs.onToggleArm())
    this.ref<HTMLButtonElement>('collapse').addEventListener('click', () => this.setCollapsed(true))
    this.ref<HTMLButtonElement>('expand').addEventListener('click', () => this.setCollapsed(false))
    this.layoutBtn.addEventListener('click', () =>
      this.setLayout(this.prefs.layout === 'push' ? 'overlay' : 'push'),
    )

    this.applyClasses()
    this.layoutBtn.setAttribute('aria-pressed', String(this.prefs.layout === 'push'))
    root.appendChild(this.el)
    // Restore a persisted push layout on mount.
    this.applyPageOffset()
  }

  private ref<T extends HTMLElement = HTMLElement>(name: string): T {
    return this.el.querySelector(`[data-ref="${name}"]`) as T
  }

  private applyClasses(): void {
    this.el.className = `qc-drawer qc-dock-${this.prefs.dock}${this.prefs.collapsed ? ' qc-collapsed' : ''}`
  }

  get collapsed(): boolean {
    return this.prefs.collapsed
  }
  get dock(): 'left' | 'right' {
    return this.prefs.dock
  }
  get layout(): 'overlay' | 'push' {
    return this.prefs.layout
  }
  /** Current on-screen footprint width, for dialog inset avoidance. */
  get footprint(): number {
    return this.prefs.collapsed ? this.railWidth : this.width
  }

  setCollapsed(collapsed: boolean, persist = true): void {
    this.prefs.collapsed = collapsed
    this.applyClasses()
    if (persist) savePrefs(this.prefs)
    // The footprint changed, so a pushed page has to be re-offset to match.
    this.applyPageOffset()
  }

  setLayout(layout: 'overlay' | 'push', persist = true): void {
    this.prefs.layout = layout
    this.layoutBtn.setAttribute('aria-pressed', String(layout === 'push'))
    if (persist) savePrefs(this.prefs)
    this.applyPageOffset()
  }

  /**
   * Push layout: offset the host page (html margin on the docked side) so content
   * reflows beside the drawer instead of underneath it. Zero residue: the html
   * element's inline style is snapshotted before the first write and restored
   * exactly by clearPageOffset() on exit or when switching back to overlay.
   */
  applyPageOffset(): void {
    if (this.prefs.layout !== 'push') {
      this.clearPageOffset()
      return
    }
    const html = document.documentElement
    if (!this.offsetApplied) {
      this.savedHtmlStyle = html.getAttribute('style')
      this.offsetApplied = true
    }
    // No transition here: Chromium can re-serialize an empty style attribute when
    // the attribute is removed while an inline margin transition is active,
    // breaking the zero-residue restore. The reflow is instant instead.
    html.style.marginRight = this.prefs.dock === 'right' ? `${this.footprint}px` : ''
    html.style.marginLeft = this.prefs.dock === 'left' ? `${this.footprint}px` : ''
  }

  clearPageOffset(): void {
    if (!this.offsetApplied) return
    const html = document.documentElement
    if (this.savedHtmlStyle === null) html.removeAttribute('style')
    else html.setAttribute('style', this.savedHtmlStyle)
    this.offsetApplied = false
    this.savedHtmlStyle = null
    // Belt and braces for the Chromium quirk above: if the engine leaves an
    // empty attribute behind, drop it on the next frame so exit is residue-free.
    requestAnimationFrame(() => {
      if (html.getAttribute('style') === '') html.removeAttribute('style')
    })
  }

  /** Occlusion: temporarily ghost the drawer (Alt-hold) without changing prefs. */
  setGhosted(on: boolean): void {
    this.el.classList.toggle('qc-ghost', on)
  }

  setScreenshot(mode: 'dom' | 'display' | false): void {
    this.shotBadge.textContent = mode === false ? 'off' : mode === 'display' ? 'display' : 'on'
    this.ref('shot').classList.toggle('qc-active', mode !== false)
  }

  render(state: State): void {
    const engaged = state.phase === 'ARMED' || state.phase === 'TARGETING' || state.phase === 'DRAWING'
    const busy = state.phase === 'SELECTED'
    const live = engaged || busy
    this.dot.className = `qc-dot${live ? ' qc-armed' : ''}`
    const pickerActive = live && state.tool === 'picker'
    const markerActive = live && state.tool === 'marker'
    this.pickerRow.classList.toggle('qc-active', pickerActive)
    this.markerRow.classList.toggle('qc-active', markerActive)
    this.ref('railPicker').classList.toggle('qc-active', live)
    this.pickerBadge.textContent = pickerActive ? (busy ? 'reporting' : 'active') : ''
    this.markerBadge.textContent = markerActive ? (state.phase === 'DRAWING' ? 'drawing' : busy ? 'reporting' : 'active') : ''
    const ctaSpan = this.cta.querySelector('span')!
    if (engaged) {
      this.cta.classList.add('qc-secondary')
      ctaSpan.textContent = state.tool === 'marker' ? 'Marker active: circle an area' : 'Pause picking'
    } else {
      this.cta.classList.remove('qc-secondary')
      ctaSpan.textContent = 'Pick an element to report'
    }
  }

  /** Show "Draft in progress" in the session list while a report has unsent text (§2). */
  setDraft(dirty: boolean): void {
    const empty = this.sessionList.querySelector('.qc-session-empty')
    if (empty) {
      empty.classList.toggle('qc-draft', dirty)
      empty.textContent = dirty
        ? 'Draft in progress…'
        : 'No reports yet. Click any element on the page to file the first one.'
    }
  }

  addSessionEntry(entry: SessionEntry, total: number, provisional = false): void {
    this.sessionList.querySelector('.qc-session-empty')?.remove()
    const { report } = entry
    const e = report.experience
    const type = e.report_type ?? (e.category === 'suggestion' ? 'suggestion' : 'bug')
    const sev = e.severity
    const row = document.createElement('div')
    row.className = 'qc-row-wrap qc-new'
    row.dataset.reportId = report.report_id
    const sevBadge =
      type === 'bug' && sev
        ? `<span class="qc-tagpill qc-sev qc-sev-${sev}"><span class="qc-sevdot"></span>${escapeHtml(sev)}</span>`
        : ''
    row.innerHTML = `
      <button class="qc-row" title="Re-highlight this element on the page">
        <span class="qc-row-top">
          <span class="qc-tagpill qc-type">${escapeHtml(type)}</span>
          <span class="qc-tagpill">${escapeHtml(e.area ?? e.category ?? '')}</span>
          ${sevBadge}
          <span class="qc-row-id">#${escapeHtml(report.report_id.slice(0, 8))}</span>
        </span>
        <span class="qc-row-desc">${escapeHtml(e.description.slice(0, 120))}</span>
        <span class="qc-row-sel">${escapeHtml(report.target.css_selector)}</span>
      </button>
      <button class="qc-row-copy qc-iconbtn" aria-label="Copy report JSON" title="Copy report JSON">${icons.layers}</button>
    `
    if (provisional) row.dataset.provisional = '1'
    row.querySelector('.qc-row')!.addEventListener('click', () => this.cbs.onRowClick(entry))
    row.querySelector('.qc-row-copy')!.addEventListener('click', () => this.cbs.onRowCopy(entry))
    this.sessionList.prepend(row)
    this.sessionCount.textContent = String(total)
    this.railCount.textContent = String(total)
  }

  removeSession(reportId: string, total: number): void {
    this.sessionList.querySelector(`[data-report-id="${CSS.escape(reportId)}"]`)?.remove()
    this.sessionCount.textContent = String(total)
    this.railCount.textContent = String(total)
    if (total === 0 && !this.sessionList.querySelector('.qc-row-wrap')) {
      const empty = document.createElement('div')
      empty.className = 'qc-session-empty'
      empty.textContent = 'No reports yet. Click any element on the page to file the first one.'
      this.sessionList.appendChild(empty)
    }
  }

  markSessionFailed(reportId: string): void {
    const row = this.sessionList.querySelector(`[data-report-id="${CSS.escape(reportId)}"] .qc-row-desc`)
    if (row) row.textContent = '⚠ Failed to send — ' + row.textContent
  }
}
